import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import { CheckCircle2, Clock, XCircle, Loader2, Phone, MapPin, Upload, FileCheck2, FileText, Image as ImageIcon, Ban, Gift } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/dates";
import CreateGiftTicketDialog from "@/components/CreateGiftTicketDialog";

const STATUS_BADGE = {
  pending: { icon: Clock, color: "#E3A857", bg: "#FFF7E6" },
  confirmed: { icon: CheckCircle2, color: "#0E8A4F", bg: "#E8F6EE" },
  rejected: { icon: XCircle, color: "#B23B3B", bg: "#FBE9E9" },
  cancelled: { icon: XCircle, color: "#635F59", bg: "#F0EDE8" },
  frozen: { icon: Clock, color: "#1E3A5F", bg: "#E6ECF4" },
  completed: { icon: CheckCircle2, color: "#0E8A4F", bg: "#E8F6EE" },
};

export default function BookingResumePage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [giftOpen, setGiftOpen] = useState(false);
  const [guideReceipts, setGuideReceipts] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api
      .get(`/bookings/${code}`)
      .then((res) => mounted && setBooking(res.data))
      .catch((err) => mounted && setError(err.response?.data?.detail || t.common.error))
      .finally(() => mounted && setLoading(false));
    // iter184 — carica ricevute di fine soggiorno emesse dalla Guida
    api
      .get(`/bookings/${code}/receipts`)
      .then((res) => mounted && setGuideReceipts(res.data?.items || []))
      .catch(() => { /* ricevute assenti: non è un errore */ });
    return () => {
      mounted = false;
    };
  }, [code, t.common.error]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error(t.resume.upload_too_big);
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);
    try {
      await api.post(`/bookings/${code}/receipt`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t.resume.upload_success);
      // refresh booking to show "uploaded" state
      const res = await api.get(`/bookings/${code}`);
      setBooking(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t.common.error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const submitCancellation = async () => {
    setCancelling(true);
    try {
      const res = await api.post(`/bookings/${code}/cancel`, { reason: cancelReason });
      const newStatus = res.data.status;
      if (newStatus === "frozen") {
        toast.success(t.resume.cancel_frozen_toast);
      } else {
        toast.success(t.resume.cancel_done_toast);
      }
      setCancelOpen(false);
      setCancelReason("");
      const fresh = await api.get(`/bookings/${code}`);
      setBooking(fresh.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t.common.error);
    } finally {
      setCancelling(false);
    }
  };

  const canCancel = booking && ["pending", "confirmed"].includes(booking.status) && (() => {
    try {
      const ci = new Date(booking.check_in);
      ci.setHours(23, 59, 59);
      return ci > new Date();
    } catch { return false; }
  })();

  const renderExtras = (b) => {
    const ex = b.extras || {};
    const items = [];
    if (ex.crib) items.push({ label: t.booking.extra_crib, amount: t.booking.free });
    if (ex.extra_bed > 0) items.push({ label: `${t.booking.extra_bed} ×${ex.extra_bed}`, amount: `€${(ex.extra_bed * 15 * b.nights).toFixed(2)}` });
    if (ex.pets) items.push({ label: t.booking.extra_pets, amount: "€10.00" });
    return items;
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Header />
      <main className="pt-28 pb-20">
        <div className="max-w-3xl mx-auto px-5 md:px-8 lg:px-12">
          {loading && (
            <div className="flex flex-col items-center py-20" data-testid="resume-loading">
              <Loader2 className="w-10 h-10 text-[#AC5D45] animate-spin" />
              <p className="mt-4 text-[#635F59]">{t.common.loading}</p>
            </div>
          )}

          {error && !loading && (
            <div className="bg-white border border-[#E8E2D9] rounded-2xl p-10 text-center" data-testid="resume-error">
              <XCircle className="w-12 h-12 text-[#B23B3B] mx-auto mb-3" />
              <h1 className="font-display text-2xl text-[#1E3A5F] mb-2">{t.resume.not_found_title}</h1>
              <p className="text-[#635F59] mb-6">{error}</p>
              <Button onClick={() => navigate("/")} className="bg-[#1E3A5F] hover:bg-[#152A45] text-white rounded-full">Home</Button>
            </div>
          )}

          {booking && !loading && (() => {
            const status = booking.status;
            const badge = STATUS_BADGE[status] || STATUS_BADGE.pending;
            const Icon = badge.icon;
            return (
              <div className="space-y-6" data-testid="resume-content">
                <div className="text-center">
                  <h1 className="font-display text-4xl md:text-5xl text-[#1E3A5F] mb-2">{t.resume.title}</h1>
                  <p className="text-[#635F59]">{t.resume.subtitle}</p>
                </div>

                {/* Status banner */}
                <div className="rounded-xl p-5 flex items-start gap-4 border" style={{ background: badge.bg, borderColor: badge.color + "40" }} data-testid={`status-${status}`}>
                  <Icon className="w-7 h-7 mt-0.5 shrink-0" style={{ color: badge.color }} />
                  <div className="flex-1">
                    <div className="font-semibold" style={{ color: badge.color }} data-testid="status-title">{t.resume.status[status] || status}</div>
                    <div className="text-sm text-[#635F59] mt-1">{t.resume.status_msg[status] || ""}</div>
                  </div>
                </div>

                {/* On-request notice: shown only when host received the booking
                    but availability was 0 — guest must wait for manual review. */}
                {booking.on_request && booking.status === "pending" && (
                  <div className="rounded-xl p-5 flex items-start gap-4 border-2" style={{ background: "#FFF4D6", borderColor: "#E3A857" }} data-testid="on-request-notice">
                    <span className="text-2xl leading-none">🟡</span>
                    <div className="flex-1">
                      <div className="font-bold text-[#7A5410]">Prenotazione su richiesta</div>
                      <div className="text-sm text-[#7A5410] mt-1 leading-relaxed">
                        Per le date selezionate la disponibilità era esaurita sui canali. L'host sta valutando personalmente la tua richiesta e ti contatterà al più presto per confermare o proporti date alternative. <strong>Non effettuare alcun pagamento</strong> finché non ricevi la conferma definitiva.
                      </div>
                    </div>
                  </div>
                )}

                {/* Booking summary card */}
                <section className="bg-white border border-[#E8E2D9] rounded-2xl p-6 md:p-8">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-display text-xl text-[#1E3A5F]">{t.booking.summary}</h2>
                    <span className="font-display text-xl font-semibold text-[#AC5D45] tracking-wider" data-testid="booking-code">{booking.code}</span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <Row label={t.booking.step2} value={booking.room_name} />
                    <Row label={t.booking.guests} value={booking.guests} />
                    <Row label={t.booking.check_in} value={formatDate(booking.check_in)} />
                    <Row label={t.booking.check_out} value={formatDate(booking.check_out)} />
                    <Row label={t.booking.nights} value={booking.nights} />
                    <Row label={t.booking.rate_type} value={booking.rate_type === "non_refundable" ? t.booking.rate_nr : t.booking.rate_standard} />
                  </div>

                  <div className="border-t border-[#E8E2D9] mt-5 pt-4 space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-[#635F59]">{t.booking.base_total}</span><span>€{(booking.base_total || 0).toFixed(2)}</span></div>
                    {booking.discount > 0 && <div className="flex justify-between text-[#AC5D45]"><span>{t.booking.discount}</span><span>−€{booking.discount.toFixed(2)}</span></div>}
                    {renderExtras(booking).length > 0 && (
                      <div className="pt-1">
                        <div className="text-xs text-[#635F59] mb-1">{t.booking.summary_extras}</div>
                        {renderExtras(booking).map((ex) => (
                          <div key={ex.label} className="flex justify-between text-xs">
                            <span>{ex.label}</span><span>{ex.amount}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-[#1E3A5F] text-base pt-2 border-t border-[#E8E2D9]">
                      <span>{t.booking.total}</span><span>€{(booking.total || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-[#1E3A5F] font-semibold">
                      <span>{t.booking.deposit}</span><span data-testid="deposit-amount">€{(booking.deposit_amount || 0).toFixed(2)}</span>
                    </div>
                    {/* Saldo restante — calcolato live, sempre visibile se > 0.
                        Aiuta l'ospite a sapere a colpo d'occhio cosa pagherà
                        all'arrivo / al check-out. */}
                    {(() => {
                      const total = Number(booking.total || 0);
                      const dep = Number(booking.deposit_amount || 0);
                      const saldo = Math.max(0, total - dep);
                      if (saldo <= 0.01) return null;
                      return (
                        <div
                          className="flex justify-between text-sm font-bold bg-amber-50 border border-amber-300 rounded -mx-2 px-3 py-2 mt-1 text-amber-900"
                          data-testid="saldo-restante"
                          title="Importo che pagherai all'arrivo"
                        >
                          <span>💰 Saldo all'arrivo</span><span>€{saldo.toFixed(2)}</span>
                        </div>
                      );
                    })()}
                  </div>
                </section>

                {/* Payment instructions — only when confirmed */}
                {status === "confirmed" && (
                  <section className="bg-white border-2 border-[#AC5D45] rounded-2xl p-6 md:p-8" data-testid="payment-instructions">
                    <h2 className="font-display text-xl text-[#1E3A5F] mb-1">{t.booking.pay_instructions}</h2>
                    <p className="text-sm text-[#635F59] mb-4">{t.resume.pay_subtitle}</p>
                    {(() => {
                      // Prefer the host-configured payment method (real
                      // IBAN, PayPal handle, QR, custom instructions)
                      // returned by the backend; fall back to the
                      // generic copy from the language pack if nothing
                      // is configured yet.
                      const pm = booking.payment_method_details || null;
                      const label = pm?.label || t.booking[`pay_${booking.payment_method}`];
                      const instructions = pm?.instructions || t.booking[`pay_inst_${booking.payment_method}`];
                      return (
                        <div className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-4 mb-4 space-y-3" data-testid="payment-method-card">
                          <div>
                            <div className="text-xs text-[#635F59] uppercase tracking-wider mb-1">{t.booking.payment}</div>
                            <div className="font-semibold text-[#1E3A5F] text-lg">{label}</div>
                          </div>
                          {pm?.holder_name && (
                            <div className="text-sm" data-testid="pm-holder">
                              <span className="text-[#635F59]">Intestato a: </span>
                              <span className="font-medium text-[#2D2926]">{pm.holder_name}</span>
                            </div>
                          )}
                          {pm?.holder_tax_id && (
                            <div className="text-sm flex items-center gap-2 flex-wrap" data-testid="pm-holder-tax-id">
                              <span className="text-[#635F59]">Codice fiscale:</span>
                              <code className="font-mono text-[12px] text-[#1E3A5F] bg-white border border-[#E8E2D9] rounded px-2 py-0.5">{pm.holder_tax_id}</code>
                              <button
                                type="button"
                                onClick={() => {
                                  try {
                                    navigator.clipboard.writeText(pm.holder_tax_id);
                                    toast.success("Codice fiscale copiato");
                                  } catch (_) { /* fall through */ }
                                }}
                                className="text-[11px] text-[#AC5D45] underline whitespace-nowrap"
                                data-testid="pm-copy-tax-id"
                              >
                                Copia
                              </button>
                            </div>
                          )}
                          {pm?.account_detail && (
                            <div className="text-sm flex items-start gap-2" data-testid="pm-account">
                              <div className="flex-1">
                                <span className="text-[#635F59] block">{(() => {
                                  const k = pm?.kind || "";
                                  if (k === "bonifico") return "IBAN:";
                                  if (k === "paypal") return "PayPal:";
                                  if (k === "satispay") return "Numero / @username:";
                                  if (k === "postepay" || k === "carta") return "Numero carta:";
                                  if (k === "sumup") return "Link / numero:";
                                  return "Dettagli:";
                                })()}</span>
                                <code className="font-mono text-[13px] text-[#1E3A5F] bg-white border border-[#E8E2D9] rounded px-2 py-1 mt-1 inline-block break-all">{pm.account_detail}</code>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  try {
                                    navigator.clipboard.writeText(pm.account_detail);
                                    toast.success("Copiato negli appunti");
                                  } catch (_) { /* fall through */ }
                                }}
                                className="text-[11px] text-[#AC5D45] underline whitespace-nowrap"
                                data-testid="pm-copy-account"
                              >
                                Copia
                              </button>
                            </div>
                          )}
                          {instructions && (
                            <div className="text-sm text-[#2D2926] leading-relaxed whitespace-pre-line border-t border-[#E8E2D9] pt-3" data-testid="pm-instructions">{instructions}</div>
                          )}
                          {pm?.qr_image && (
                            <div className="border-t border-[#E8E2D9] pt-3 text-center" data-testid="pm-qr">
                              <p className="text-[11px] text-[#635F59] mb-2">Inquadra per pagare</p>
                              <img src={pm.qr_image} alt="QR pagamento" className="max-w-[200px] mx-auto rounded border border-[#E8E2D9]" />
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    <div className="flex items-center justify-between bg-[#AC5D45]/10 border border-[#AC5D45]/30 rounded-lg p-4">
                      <span className="font-semibold text-[#1E3A5F]">{t.booking.deposit}</span>
                      <span className="font-display text-2xl font-bold text-[#AC5D45]" data-testid="deposit-amount-cta">€{(booking.deposit_amount || 0).toFixed(2)}</span>
                    </div>
                    <p className="text-xs text-[#635F59] mt-4 text-center">{t.resume.after_payment}</p>
                  </section>
                )}

                {/* Receipt upload — only when confirmed */}
                {status === "confirmed" && (
                  <section className="bg-white border border-[#E8E2D9] rounded-2xl p-6 md:p-8" data-testid="receipt-upload-section">
                    <h2 className="font-display text-xl text-[#1E3A5F] mb-1">{t.resume.upload_title}</h2>
                    <p className="text-sm text-[#635F59] mb-5">{t.resume.upload_subtitle}</p>

                    {booking.receipt ? (
                      <div className="bg-[#E8F6EE] border border-[#0E8A4F]/30 rounded-lg p-4 flex items-start gap-3" data-testid="receipt-uploaded">
                        <FileCheck2 className="w-6 h-6 text-[#0E8A4F] mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-[#0E8A4F]">{t.resume.upload_done_title}</div>
                          <div className="text-xs text-[#635F59] mt-1">{t.resume.upload_done_msg}</div>
                          <div className="mt-2 flex items-center gap-2 text-xs text-[#2D2926]">
                            {booking.receipt.mime?.startsWith("image/") ? <ImageIcon className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />}
                            <span className="truncate">{booking.receipt.filename}</span>
                            <span className="text-[#635F59]">· {(booking.receipt.size / 1024).toFixed(0)} KB</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            data-testid="upload-replace-btn"
                            className="mt-3 text-xs text-[#AC5D45] hover:underline disabled:opacity-50"
                          >
                            {uploading ? t.common.loading : t.resume.upload_replace}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        data-testid="upload-trigger-btn"
                        className="w-full border-2 border-dashed border-[#AC5D45]/40 hover:border-[#AC5D45] hover:bg-[#AC5D45]/5 rounded-xl p-8 transition flex flex-col items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {uploading ? (
                          <Loader2 className="w-10 h-10 text-[#AC5D45] animate-spin" />
                        ) : (
                          <Upload className="w-10 h-10 text-[#AC5D45]" />
                        )}
                        <div className="text-center">
                          <div className="font-semibold text-[#1E3A5F]">{uploading ? t.resume.upload_loading : t.resume.upload_cta}</div>
                          <div className="text-xs text-[#635F59] mt-1">{t.resume.upload_hint}</div>
                        </div>
                      </button>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf"
                      onChange={handleFileSelect}
                      className="hidden"
                      data-testid="upload-file-input"
                    />
                  </section>
                )}

                {/* Contact card */}
                <section className="bg-[#1E3A5F] text-white rounded-2xl p-6 md:p-8">
                  <h2 className="font-display text-xl mb-4">{t.resume.need_help}</h2>
                  <div className="space-y-2 text-sm">
                    <a href="tel:+390965181 7260" className="flex items-center gap-3 hover:text-[#E3A857] transition" data-testid="contact-phone">
                      <Phone className="w-4 h-4" /> +39 0965 181 7260
                    </a>
                    <div className="flex items-start gap-3 text-white/80">
                      <MapPin className="w-4 h-4 mt-0.5" />
                      <span>Traversa IV Maldariti, 29b — 89131 Reggio Calabria (RC)</span>
                    </div>
                  </div>
                </section>

                {/* Gift Ticket — only when booking is confirmed or completed */}
                {(status === "confirmed" || status === "completed") && (
                  <section className="bg-gradient-to-br from-[#AC5D45]/10 via-[#FFF5EE] to-[#F5F1EA] border border-[#AC5D45]/40 rounded-2xl p-6 md:p-8" data-testid="gift-ticket-section">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#AC5D45] flex items-center justify-center shrink-0">
                        <Gift className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h2 className="font-display text-xl text-[#1E3A5F] mb-1">Vuoi fare un regalo?</h2>
                        <p className="text-sm text-[#635F59] mb-4">
                          Crea un ticket condivisibile con tutti i dettagli della prenotazione
                          <strong> senza il prezzo</strong>. Perfetto da inviare via WhatsApp o email a chi vuoi sorprendere.
                        </p>
                        <Button
                          onClick={() => setGiftOpen(true)}
                          data-testid="open-gift-dialog-btn"
                          className="bg-[#AC5D45] hover:bg-[#8A4936] text-white"
                        >
                          <Gift className="w-4 h-4 mr-2" /> Crea Ticket Regalo
                        </Button>
                      </div>
                    </div>
                  </section>
                )}

                {/* Cancel button — only if cancellable */}
                {canCancel && (
                  <section className="text-center" data-testid="cancel-section">
                    <Button
                      variant="outline"
                      onClick={() => setCancelOpen(true)}
                      data-testid="cancel-booking-btn"
                      className="text-rose-700 border-rose-300 hover:bg-rose-50"
                    >
                      <Ban className="w-4 h-4 mr-2" /> {t.resume.cancel_btn}
                    </Button>
                    {booking.rate_type === "non_refundable" && booking.status === "confirmed" && (
                      <p className="text-xs text-[#635F59] mt-2 max-w-md mx-auto">{t.resume.cancel_nr_note}</p>
                    )}
                  </section>
                )}
              </div>
            );
          })()}
        </div>
      </main>

      {/* CANCEL DIALOG */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent data-testid="cancel-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1E3A5F]">{t.resume.cancel_title}</DialogTitle>
            <DialogDescription>
              {booking?.rate_type === "non_refundable" && booking?.status === "confirmed"
                ? t.resume.cancel_confirm_nr
                : t.resume.cancel_confirm}
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-xs text-[#635F59] mb-1 block">{t.resume.cancel_reason_label}</label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              placeholder={t.resume.cancel_reason_placeholder}
              data-testid="cancel-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)} data-testid="cancel-dialog-back">{t.booking.back}</Button>
            <Button
              onClick={submitCancellation}
              disabled={cancelling}
              data-testid="cancel-dialog-confirm"
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {cancelling ? t.common.loading : t.resume.cancel_confirm_btn}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CreateGiftTicketDialog
        open={giftOpen}
        onOpenChange={setGiftOpen}
        bookingCode={booking?.code}
        existingGift={booking?.gift_ticket}
        defaultSender={[booking?.guest?.first_name, booking?.guest?.last_name].filter(Boolean).join(" ")}
      />

      {/* iter184 — Ricevute di fine soggiorno emesse dalla Guida Digitale.
          Cliccabili dall'ospite per ri-scaricare il PDF anche mesi dopo. */}
      {guideReceipts.length > 0 && (
        <div className="max-w-3xl mx-auto px-4 pb-8">
          <div className="bg-white border border-[#E8E2D9] rounded-2xl shadow-sm p-5">
            <h3 className="font-display text-lg text-[#1E3A5F] mb-1">
              📄 Le mie ricevute
            </h3>
            <p className="text-xs text-[#635F59] mb-4">
              Ricevute riepilogative emesse a fine soggiorno. Puoi scaricarle in qualsiasi momento.
            </p>
            <div className="space-y-2" data-testid="guide-receipts-list">
              {guideReceipts.map((r) => (
                <div
                  key={r.receipt_id}
                  className="flex items-center justify-between gap-3 flex-wrap border border-[#E8E2D9] rounded-lg p-3 bg-[#FDFBF7]"
                  data-testid={`guide-receipt-${r.receipt_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-[#2D2926]">
                      Ricevuta N. <b>{r.progressive || `${r.number}/${r.year}`}</b>
                      {r.is_final_checkout && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300">Fine soggiorno</span>
                      )}
                    </div>
                    <div className="text-xs text-[#635F59]">
                      {r.issued_at ? new Date(r.issued_at).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }) : ""}
                      {" · "}Totale <b className="text-[#AC5D45]">€ {Number(r.total || 0).toFixed(2)}</b>
                      {r.payment_method && <> · {r.payment_method}</>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {r.preview_url && (
                      <a
                        href={r.preview_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs px-3 py-1.5 rounded border border-[#D4CFC5] hover:bg-white transition"
                        data-testid={`receipt-preview-${r.receipt_id}`}
                      >
                        Anteprima
                      </a>
                    )}
                    {r.download_url && (
                      <a
                        href={r.download_url}
                        download
                        className="text-xs px-3 py-1.5 rounded bg-[#AC5D45] hover:bg-[#8A4936] text-white font-medium"
                        data-testid={`receipt-download-${r.receipt_id}`}
                      >
                        Scarica PDF
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between py-1">
      <span className="text-[#635F59]">{label}</span>
      <span className="font-medium text-[#2D2926] text-right">{value}</span>
    </div>
  );
}
